import React, { useState, useEffect } from 'react';
import './TradingStats.css';

const TradingStats = ({ trades, winRate, botRunning }) => {
  const [stats, setStats] = useState({
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    avgProfit: 0,
    avgLoss: 0,
    profitFactor: 0,
    maxDrawdown: 0,
    currentStreak: 0,
    bestStreak: 0,
    dcaLevels: 0,
    tpHits: 0
  });

  // Simuler des statistiques de trading
  useEffect(() => {
    if (botRunning) {
      const interval = setInterval(() => {
        setStats(prev => {
          const newTotalTrades = prev.totalTrades + (Math.random() > 0.7 ? 1 : 0);
          const isWin = Math.random() > 0.4; // 60% de taux de réussite
          
          return {
            ...prev,
            totalTrades: newTotalTrades,
            winningTrades: prev.winningTrades + (isWin && newTotalTrades > prev.totalTrades ? 1 : 0),
            losingTrades: prev.losingTrades + (!isWin && newTotalTrades > prev.totalTrades ? 1 : 0),
            avgProfit: 15.5 + (Math.random() - 0.5) * 5,
            avgLoss: -8.2 + (Math.random() - 0.5) * 3,
            profitFactor: 1.8 + (Math.random() - 0.5) * 0.4,
            maxDrawdown: -12.5 + (Math.random() - 0.5) * 5,
            currentStreak: Math.floor(Math.random() * 8),
            bestStreak: Math.max(prev.bestStreak, Math.floor(Math.random() * 15)),
            dcaLevels: Math.floor(Math.random() * 5),
            tpHits: prev.tpHits + (Math.random() > 0.8 ? 1 : 0)
          };
        });
      }, 10000); // Mise à jour toutes les 10 secondes

      return () => clearInterval(interval);
    }
  }, [botRunning]);

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatCurrency = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}$${Math.abs(value).toFixed(2)}`;
  };

  const getWinRate = () => {
    if (stats.totalTrades === 0) return 0;
    return (stats.winningTrades / stats.totalTrades) * 100;
  };

  const getStatColor = (value, isPercentage = false) => {
    if (isPercentage) {
      if (value > 60) return '#00ff88';
      if (value > 40) return '#ffc107';
      return '#ff4757';
    }
    
    if (value > 0) return '#00ff88';
    if (value < 0) return '#ff4757';
    return '#ffffff';
  };

  return (
    <div className="trading-stats">
      <div className="stats-header">
        <h3>📊 Statistiques de Trading</h3>
        <div className={`bot-indicator ${botRunning ? 'running' : 'stopped'}`}>
          <div className="indicator-dot"></div>
          {botRunning ? 'EN COURS' : 'ARRÊTÉ'}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-group">
          <h4>🎯 Performance</h4>
          <div className="stat-items">
            <div className="stat-item">
              <span className="stat-label">Trades Totaux:</span>
              <span className="stat-value">{stats.totalTrades}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Taux de Réussite:</span>
              <span 
                className="stat-value"
                style={{ color: getStatColor(getWinRate(), true) }}
              >
                {formatPercentage(getWinRate())}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Profit Factor:</span>
              <span 
                className="stat-value"
                style={{ color: getStatColor(stats.profitFactor - 1) }}
              >
                {stats.profitFactor.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="stat-group">
          <h4>💰 Profits/Pertes</h4>
          <div className="stat-items">
            <div className="stat-item">
              <span className="stat-label">Profit Moyen:</span>
              <span 
                className="stat-value"
                style={{ color: getStatColor(stats.avgProfit) }}
              >
                {formatCurrency(stats.avgProfit)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Perte Moyenne:</span>
              <span 
                className="stat-value"
                style={{ color: getStatColor(stats.avgLoss) }}
              >
                {formatCurrency(stats.avgLoss)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Max Drawdown:</span>
              <span 
                className="stat-value"
                style={{ color: getStatColor(stats.maxDrawdown) }}
              >
                {formatPercentage(stats.maxDrawdown)}
              </span>
            </div>
          </div>
        </div>

        <div className="stat-group">
          <h4>🔥 Séries</h4>
          <div className="stat-items">
            <div className="stat-item">
              <span className="stat-label">Série Actuelle:</span>
              <span className="stat-value">{stats.currentStreak}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Meilleure Série:</span>
              <span className="stat-value highlight">{stats.bestStreak}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Trades Gagnants:</span>
              <span className="stat-value win">{stats.winningTrades}</span>
            </div>
          </div>
        </div>

        <div className="stat-group">
          <h4>⚙️ Stratégie V3.6</h4>
          <div className="stat-items">
            <div className="stat-item">
              <span className="stat-label">DCA Niveaux:</span>
              <span className="stat-value">{stats.dcaLevels}/20</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">TP Atteints:</span>
              <span className="stat-value tp">{stats.tpHits}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Seuil DCA:</span>
              <span className="stat-value">-3.0%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="performance-summary">
        <h4>📈 Résumé de Performance</h4>
        <div className="summary-grid">
          <div className="summary-item positive">
            <div className="summary-icon">📈</div>
            <div className="summary-content">
              <div className="summary-title">Trades Gagnants</div>
              <div className="summary-value">{stats.winningTrades}</div>
            </div>
          </div>
          
          <div className="summary-item negative">
            <div className="summary-icon">📉</div>
            <div className="summary-content">
              <div className="summary-title">Trades Perdants</div>
              <div className="summary-value">{stats.losingTrades}</div>
            </div>
          </div>
          
          <div className="summary-item neutral">
            <div className="summary-icon">🎯</div>
            <div className="summary-content">
              <div className="summary-title">Précision</div>
              <div className="summary-value">{formatPercentage(getWinRate())}</div>
            </div>
          </div>
        </div>
      </div>

      {!botRunning && (
        <div className="inactive-message">
          <span>⏸️</span>
          <p>Bot inactif - Les statistiques seront mises à jour lors du prochain démarrage</p>
        </div>
      )}
    </div>
  );
};

export default TradingStats;
